<template>
  <v-card :loading="loadingCard">
    <v-card-title>
      <v-row justify="space-between">
        <span class="font-weight-light" style="font-size: 16pt">{{ $t('label.shipmentDetails') }}</span>
        <v-btn :disabled="loadingCard" icon @click.stop="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-row no-gutters>
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('label.search')"
            dense
            outlined
          ></v-text-field>
        </v-col>
      </v-row>
      <v-data-table
        :headers="headers"
        :items="shipmentDetails"
        :loading="loadingCard"
        :options="options"
        :search="search"
        dense>
        <template v-slot:item.purchaseOrder="{ item }">
          <span>{{ item.purchaseOrder }}</span>&nbsp;<span v-show="item.vendorOrder.length > 0">({{ item.vendorOrder }})</span>
        </template>
        <template v-slot:item.line="{ item }">
          <span>{{ item.line }}</span>&nbsp;<span v-show="item.vendorShippingLine > 0">({{ item.vendorShippingLine }})</span>
        </template>
        <template v-slot:[`item.options`]="{ item }">
          <v-btn v-if="item.isBlocked" class="btn-action" icon :title="$t('label.lineBlocked')" small depressed>
            <v-icon color="error">mdi-exclamation-thick</v-icon>
          </v-btn>
          <v-btn class="btn-action" v-if="!isBlocked && item.isConsignment" :disabled="item.hasCustomDistribution || shipmentStatus === 2" icon :title="$t('label.print')" small depressed @click.stop="downloadLabelDetailConsignment(item)">
            <v-icon :color="item.printStatus === 2 ? 'success' : 'error'">mdi-printer</v-icon>
          </v-btn>
          <v-btn class="btn-action" v-if="!isBlocked  && !item.isConsignment" :disabled="item.hasCustomDistribution || shipmentStatus === 2" icon :title="$t('label.print')" small depressed @click.stop="downloadLabelDetail(item)">
            <v-icon :color="item.printStatus === 2 ? 'success' : 'error'">mdi-printer</v-icon>
          </v-btn>
          <v-btn :disabled="shipmentStatus === 2 || !item.canCustomizeQuantities" class="btn-action" icon :title="$t('label.customDistribution')" small depressed @click.stop="openCustomDistributionDialog(item)">
            <v-icon color="primary">mdi-layers</v-icon>
          </v-btn>
        </template>
        <template v-slot:[`item.isCancelled`]="{ item }">
          <v-chip label color="blue" text-color="white" v-if="item.isCancelled">{{ $t('label.yes') }}</v-chip>
          <v-chip label v-else>{{ $t('label.no') }}</v-chip>
        </template>
      </v-data-table>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn small depressed @click.stop="close" :disabled="loadingCard">{{ $t('label.close') }}</v-btn>
    </v-card-actions>
    <v-dialog v-model="isDistributionDialogOpen" persistent scrollable width="1200">
      <custom-distribution :shipmentDetailId="selectedDetail.shipmentDetailId" @close="closeDistribution()"></custom-distribution>
    </v-dialog>
  </v-card>
</template>

<script>
import { loading, showMessage } from '../../../mixins'
import { saveAs } from 'file-saver'
import CustomDistribution from '../shipments/CustomDistribution.vue'

export default {
  name: 'ShipmentDetail',
  components: {
    CustomDistribution
  },
  props: {
    shipmentId: {
      type: Number
    },
    shipmentStatus: {
      type: Number
    },
    print: {
      type: Boolean,
      default: false
    }
  },
  mixins: [loading, showMessage],
  data: () => ({
    shipmentDetails: [],
    options: {
      sortBy: ['id'],
      sortDesc: [true],
      itemsPerPage: 20
    },
    search: '',
    loadingCard: false,
    selectedDetail: {
      shipmentDetailId: 0
    },
    isDistributionDialogOpen: false
  }),
  computed: {
    headers () {
      var headers = [
        { text: this.$t('label.release'), value: 'releaseNumber', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.partNumber'), value: 'partNumber', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.description'), value: 'description', align: 'center', sortable: true, filterable: true },
        { text: 'PO', value: 'purchaseOrder', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.line'), value: 'line', align: 'center', sortable: true, filterable: true },
        { text: 'SNP', value: 'snp', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.quantityToShip'), value: 'quantityToShip', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.receivedQuantity'), value: 'confirmedQuantity', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.isCancelled'), value: 'isCancelled', align: 'center', sortable: true, filterable: true },
        { text: this.$t('label.options'), value: 'options', align: 'center', sortable: true, filterable: true }
      ]

      return headers
    },
    isBlocked () {
      return this.shipmentDetails.findIndex(s => s.isBlocked === 1) > -1
    }
  },
  methods: {
    closeDistribution () {
      this.getShipmentDetails()
      this.isDistributionDialogOpen = false
    },
    getShipmentDetails () {
      this.loadingCard = true
      this.$proxy.get(`api/edi/shipments/${this.shipmentId}/details`)
        .then((r) => {
          this.shipmentDetails = r.data
        })
        .catch((e) => {
          this.showHttpErrorMessage(e)
        })
        .finally(() => {
          this.loadingCard = false
        })
    },
    close () {
      this.$emit('close')
    },
    openCustomDistributionDialog (item) {
      this.getShipmentDetails()
      this.selectedDetail = Object.assign({}, item)
      this.isDistributionDialogOpen = true
    },
    downloadLabelDetailConsignment (item) {
      this.loadingCard = true
      this.$proxy.getDownload(`api/edi/shipments/${this.shipmentId}/labelconsignment?shipmentDetailId=${item.shipmentDetailId}`)
        .then((r) => {
          saveAs(r.data, `Shipment-${this.shipmentId}.pdf`)
        })
        .catch((e) => {
          console.log(e)
          this.showHttpErrorMessage(e)
        })
        .finally(() => {
          this.loadingCard = false
        })
    },
    downloadLabelDetail (item) {
      this.loadingCard = true
      this.$proxy.getDownload(`api/edi/shipments/${this.shipmentId}/label?shipmentDetailId=${item.shipmentDetailId}`)
        .then((r) => {
          saveAs(r.data, `Shipment-${this.shipmentId}.pdf`)
        })
        .catch((e) => {
          console.log(e)
          this.showHttpErrorMessage(e)
        })
        .finally(() => {
          this.loadingCard = false
        })
    }
  },
  watch: {
    shipmentId: function (newValue, oldValue) {
      if (newValue) {
        this.shipmentDetails = []
        this.getShipmentDetails()
      }
    }
  },
  mounted () {
    this.getShipmentDetails()
  }

}
</script>

<style>

</style>
